import { useEffect, useRef, useState } from 'react'
import {
  isUserDateInputValid,
  USER_DATE_INPUT_MAX,
  USER_DATE_INPUT_MIN,
  USER_DATE_YEAR_RANGE_LABEL,
} from '@crm/shared'
import { IconBell } from './kpControlIcons'

interface Props {
  /** Заголовок — по умолчанию «Следующий контакт с клиентом». */
  title?: string
  /** Начальные значения (режим редактирования активного напоминания). */
  initialDueAt?: string | null
  initialNote?: string | null
  /** Текст кнопки закрытия без сохранения: «Пропустить» после отправки КП. */
  cancelLabel?: string
  onConfirm: (dueAt: string, note: string) => Promise<void> | void
  onCancel: () => void
}

/**
 * Модалка «Контроль КП»: дата следующего контакта с клиентом + заметка.
 * Открывается после «КП отправлено» (пропускаемая) и из блока «Контроль КП»
 * (назначить/изменить). Дата обязательна, заметка — нет. Дефолта даты нет
 * сознательно (решение Сергея 16.07) — менеджер выбирает сам.
 */
export function FollowUpModal({
  title = 'Следующий контакт с клиентом',
  initialDueAt = null,
  initialNote = null,
  cancelLabel = 'Отмена',
  onConfirm,
  onCancel,
}: Props) {
  const [dueAt, setDueAt] = useState(() => (initialDueAt ? initialDueAt.slice(0, 10) : ''))
  const [note, setNote] = useState(initialNote ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const dateRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    dateRef.current?.focus()
  }, [])

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onCancel])

  const handleConfirm = async () => {
    if (!dueAt) return
    // Нативный date-input позволяет ввести год из 5-6 цифр (62026) — сервер
    // такой год отвергает; ловим до запроса, чтобы сотрудник сразу увидел где ошибка.
    if (!isUserDateInputValid(dueAt)) {
      setError(
        `Дата контакта вне допустимого диапазона (${USER_DATE_YEAR_RANGE_LABEL}) — проверьте год`,
      )
      return
    }
    setSaving(true)
    setError('')
    try {
      await onConfirm(dueAt, note.trim())
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string }; message?: string } } })
          ?.response?.data?.error?.message ??
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Не удалось сохранить'
      setError(msg)
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel()
      }}
    >
      <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-md mx-4">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-800">
            <IconBell className="w-4 h-4 text-brand-600" />
            {title}
          </h3>
          <button
            onClick={onCancel}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none disabled:opacity-40"
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-xs text-gray-500">
            Внутреннее напоминание: когда связаться с клиентом по этому КП. Видно вам, РОПу и
            администратору — клиент его не видит.
          </p>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Дата контакта</span>
            <input
              ref={dateRef}
              type="date"
              value={dueAt}
              min={USER_DATE_INPUT_MIN}
              max={USER_DATE_INPUT_MAX}
              onChange={(e) => setDueAt(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Заметка (необязательно)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault()
                  void handleConfirm()
                }
              }}
              rows={3}
              maxLength={2000}
              placeholder="Созвониться с клиентом, узнать по поводу решения…"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"
            />
          </label>

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => void handleConfirm()}
            disabled={!dueAt || saving}
            className="px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  )
}
